import { User } from '@microsoft/microsoft-graph-types';

export class MicrosoftGraphService {
  private baseUrl = 'https://graph.microsoft.com/v1.0';

  constructor(private getAccessToken: () => Promise<string | null>) { }

  private async makeRequest<T>(endpoint: string): Promise<T> {
    const token = await this.getAccessToken();
    if (!token) {
      throw new Error('No access token available');
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Graph API request failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  private async makeFileRequest(endpoint: string): Promise<Blob> {
    const token = await this.getAccessToken();
    if (!token) {
      throw new Error('No access token available');
    }

    const cb = endpoint.includes('?') ? `&cb=${Date.now()}` : `?cb=${Date.now()}`;
    const response = await fetch(`${this.baseUrl}${endpoint}${cb}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      },
      cache: 'no-store'
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Resource not found');
      }
      throw new Error(`Graph API request failed: ${response.status} ${response.statusText}`);
    }

    return response.blob();
  }

  async getCurrentUser(): Promise<User> {
    return this.makeRequest<User>('/me');
  }

  async getCurrentUserDetailed(): Promise<User> {
    const select = [
      'id', 'displayName', 'givenName', 'surname', 'mail', 'userPrincipalName',
      'jobTitle', 'department', 'officeLocation', 'businessPhones', 'mobilePhone',
      'preferredLanguage', 'companyName', 'employeeId', 'country', 'city',
      'streetAddress', 'postalCode'
    ].join(',');

    return this.makeRequest<User>(`/me?$select=${select}`);
  }

  async getUserPhoto(_size: string = '96x96'): Promise<string | null> {
    try {
      const blob = await this.makeFileRequest('/me/photo/$value');
      return URL.createObjectURL(blob);
    } catch (error) {
      if (error instanceof Error && error.message.includes('not found')) {
        return null;
      }
      throw error;
    }
  }

  async getUserPhotoMetadata() {
    return this.makeRequest('/me/photo');
  }

  static getDisplayName(user: Partial<User>): string {
    return user.displayName || user.userPrincipalName || 'Unknown User';
  }

  static getEmail(user: Partial<User>): string {
    return user.mail || user.userPrincipalName || '';
  }

  static getFullName(user: Partial<User>): string {
    if (user.displayName) return user.displayName;

    const firstName = user.givenName || '';
    const lastName = user.surname || '';
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || user.userPrincipalName || 'Unknown User';
  }
}
